// import { useDeleteUser, useGetAllUsers } from "./useOrgUsers";
import { createColumnHelper } from "@tanstack/react-table";
import ReactTanstackTableUi from "../../common/ReactTanstackTableUi";
import { useMaintenancePage } from "../../api/api-hooks";
import { useState } from "react";
import useCommonStore from "../../store/CommonStore";
import { getDateRangeFromSelectedValue } from "../../common/Constants";
import { usePagination } from "../hooks/usePagination";
import AddMaintenanceModal from "./AddMaintenanceModal";
import { useAuth } from "../../lib/AuthContext";

function prepareColumns(columnHelper: any, handleAction: any) {
  return [
    columnHelper.accessor((row: any) => row.type, {
      id: "type",
      header: () => <div>Type</div>,
      cell: (info: any) => (
        <div className="font-semibold text-gray-800 text-base">
          {info.row.original?.type || "--"}
        </div>
      ),
    }),
    columnHelper.accessor((row: any) => row.app_name, {
      id: "target",
      header: () => <div>Target</div>,
      cell: (info: any) => (
        <div className="font-semibold text-gray-800 text-base">
          {info.row.original?.app_name || "--"}
        </div>
      ),
    }),
    columnHelper.accessor((row: any) => row.start_time, {
      id: "window",
      header: () => <div>Window (IST)</div>,
      cell: (info: any) => {
        const { start_time, end_time } = info.row.original;
        return (
          <div className="text-sm text-gray-700">
            {new Date(start_time).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} →{" "}
            {new Date(end_time).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
          </div>
        );
      },
    }),
    columnHelper.accessor((row: any) => row.username, {
      id: "requested_by",
      header: () => <div>Requested By</div>,
      cell: (info: any) => (
        <div className="font-semibold text-gray-800 text-base">
          {info.row.original?.username || "--"}
        </div>
      ),
    }),
    columnHelper.accessor((row: any) => row.status, {
      id: "status",
      header: () => <div>Status</div>,
      cell: (info: any) => {
        const status = info.row.original?.status;
        const getStatusClasses = (status: string) => {
          switch (status) {
            case "approved":
              return "bg-green-100 text-green-700";
            case "rejected":
              return "bg-red-100 text-red-700";
            case "pending":
              return "bg-yellow-100 text-yellow-700";
            default:
              return "bg-gray-100 text-gray-700";
          }
        };

        return (
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusClasses(
              status
            )}`}
          >
            {status}
          </span>
        );
      },
    }),
    columnHelper.accessor((row: any) => row.created_at, {
      id: "created",
      header: () => <div>Created</div>,
      cell: (info: any) => (
        <div className="text-sm text-gray-700">
          {new Date(info.row.original?.created_at).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
          })}
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => <div>Actions</div>,
      cell: (info: any) => {
        const { status, id } = info.row.original;
        return (
          <div className="flex items-center space-x-2">
            {status === "pending" ? (
              <>
                <button
                  onClick={() => handleAction(id, "approved")}
                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Approve
                </button>
                <button
                  onClick={() => handleAction(id, "rejected")}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reject
                </button>
              </>
            ) : (
              <span className="text-gray-400 text-sm">No actions</span>
            )}
          </div>
        );
      },
    }),
  ];
}


function MaintenanceListTable() {
  const { selectedValue } = useCommonStore();

  const dateRange = getDateRangeFromSelectedValue(selectedValue);

  const { pageIndex, pageSize, getPaginationInfo } = usePagination();

  const isAllData = dateRange?.isAllData ?? false;
  const startDate = isAllData ? undefined : dateRange?.startDate ?? "";
  const endDate = isAllData ? undefined : dateRange?.endDate ?? "";
  const { hasAuthenticated } = useAuth();

  const apiURL = process.env.REACT_APP_API_URL

  const { data: users, isLoading, refetch: refetchmaintenancePage } = useMaintenancePage(
    pageIndex + 1,
    pageSize,
    startDate,
    endDate,
    isAllData
  );

  const apiData = users?.items || [];
  const columnHelper: any = createColumnHelper();

  const paginationInfo = getPaginationInfo(users);

  async function handleAction(id: string, action: "approved" | "rejected") {
    if (!hasAuthenticated) return;
    try {
      const response = await fetch(`${apiURL}/events/${id}`, {
        method: "PATCH",
        headers: {
          "accept": "application/json",
          "Content-Type": "application/json",
          "Token": hasAuthenticated,
        },
        body: JSON.stringify({ status: action }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action}: ${response.status}`);
      }

      const data = await response.json();
      await refetchmaintenancePage();
      return data;
    } catch (error) {
      console.error(`Error while ${action}:`, error);
    }
  }

  const columns = prepareColumns(columnHelper, handleAction);

  const [isAddOpen, setIsAddOpen] = useState(false);

  async function handleCreateMaintenance(payload: any) {
    await refetchmaintenancePage();
    setIsAddOpen(false);
  }

  return (
    <>
      <ReactTanstackTableUi
        data={apiData}
        columns={columns}
        noDataMessage="Maintenance"
        tableTitle="Maintenance"
        disableRowHeight={true}
        pagination={true}
        externalPagination={paginationInfo}
        isLoading={isLoading}
        isAddBtn="Add Maintenance"
        onAddBtnClick={() => setIsAddOpen(true)}
      />

      <AddMaintenanceModal
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onCreate={handleCreateMaintenance}
      />
    </>
  );
}

function Maintenance() {
  return (
    <div className="flex flex-col gap-3">
      <MaintenanceListTable />
    </div>
  );
}

export default Maintenance;
